import { createFileRoute } from '@tanstack/react-router';
import { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Download, Upload, Database, AlertTriangle, Key, Copy, Check, Trash2 } from 'lucide-react';
import { Header, PageContainer } from '@/components/layout';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Input,
  Select,
  Modal,
  Badge,
  ConfirmDialog,
} from '@/components/ui';
import { useAuthStore } from '@/stores/auth';
import { api } from '@/services/api';
import { useApiKeys, useCreateApiKey, useDeleteApiKey } from '@/hooks/useApiKeys';
import type { ApiKey } from '@aelita/shared';
import { PreferencesCard } from '@/components/settings/PreferencesCard';

export const Route = createFileRoute('/settings')({
  component: SettingsPage,
});

interface ProfileFormData {
  firstName: string;
  lastName: string;
  phone: string;
  title: string;
  department: string;
}

interface PasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

function SettingsPage() {
  const { user, fetchMe } = useAuthStore();
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors, isSubmitting: isProfileSubmitting },
  } = useForm<ProfileFormData>({
    defaultValues: {
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      phone: '',
      title: user?.title ?? '',
      department: user?.department ?? '',
    },
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    reset: resetPassword,
    formState: { errors: passwordErrors, isSubmitting: isPasswordSubmitting },
    watch,
  } = useForm<PasswordFormData>();

  const newPassword = watch('newPassword');

  const onProfileSubmit = async (data: ProfileFormData) => {
    setProfileSuccess(false);
    setProfileError(null);
    try {
      await api.put('/auth/profile', {
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone || null,
        title: data.title || null,
        department: data.department || null,
      });
      await fetchMe();
      setProfileSuccess(true);
    } catch (error: any) {
      setProfileError(error.message ?? 'Failed to update profile');
    }
  };

  const onPasswordSubmit = async (data: PasswordFormData) => {
    setPasswordSuccess(false);
    setPasswordError(null);
    try {
      await api.post('/auth/change-password', {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      setPasswordSuccess(true);
      resetPassword();
    } catch (error: any) {
      setPasswordError(error.message ?? 'Failed to change password');
    }
  };

  return (
    <>
      <Header title="Settings" subtitle="Manage your account and preferences" />
      <PageContainer>
        <div className="max-w-2xl space-y-6">
          {/* Profile Settings */}
          <Card>
            <CardHeader>
              <h2 className="font-semibold">Profile Settings</h2>
            </CardHeader>
            <CardBody>
              <form onSubmit={handleProfileSubmit(onProfileSubmit)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="First Name"
                    {...registerProfile('firstName', { required: 'First name is required' })}
                    error={profileErrors.firstName?.message}
                  />
                  <Input
                    label="Last Name"
                    {...registerProfile('lastName', { required: 'Last name is required' })}
                    error={profileErrors.lastName?.message}
                  />
                </div>
                <Input label="Email" value={user?.email ?? ''} disabled />
                <Input
                  label="Phone"
                  {...registerProfile('phone')}
                  placeholder="Your phone number"
                />
                <Input label="Title" {...registerProfile('title')} placeholder="Your job title" />
                <Input
                  label="Department"
                  {...registerProfile('department')}
                  placeholder="Your department"
                />
                {profileSuccess && (
                  <p className="text-sm text-green-600 dark:text-green-400">Profile updated successfully!</p>
                )}
                {profileError && <p className="text-sm text-red-600 dark:text-red-400">{profileError}</p>}
                <div className="pt-2">
                  <Button type="submit" isLoading={isProfileSubmitting}>
                    Save Changes
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>

          {/* Password */}
          <Card>
            <CardHeader>
              <h2 className="font-semibold">Change Password</h2>
            </CardHeader>
            <CardBody>
              <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-4">
                <Input
                  label="Current Password"
                  type="password"
                  {...registerPassword('currentPassword', {
                    required: 'Current password is required',
                  })}
                  error={passwordErrors.currentPassword?.message}
                  placeholder="Enter current password"
                />
                <Input
                  label="New Password"
                  type="password"
                  {...registerPassword('newPassword', {
                    required: 'New password is required',
                    minLength: { value: 8, message: 'Password must be at least 8 characters' },
                  })}
                  error={passwordErrors.newPassword?.message}
                  placeholder="Enter new password"
                />
                <Input
                  label="Confirm New Password"
                  type="password"
                  {...registerPassword('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: (value) => value === newPassword || 'Passwords do not match',
                  })}
                  error={passwordErrors.confirmPassword?.message}
                  placeholder="Confirm new password"
                />
                {passwordSuccess && (
                  <p className="text-sm text-green-600 dark:text-green-400">Password changed successfully!</p>
                )}
                {passwordError && <p className="text-sm text-red-600 dark:text-red-400">{passwordError}</p>}
                <div className="pt-2">
                  <Button type="submit" isLoading={isPasswordSubmitting}>
                    Update Password
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>

          <PreferencesCard />

          {/* Account Info */}
          <Card>
            <CardHeader>
              <h2 className="font-semibold">Account Information</h2>
            </CardHeader>
            <CardBody>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500 dark:text-gray-400">Role</dt>
                  <dd className="font-medium text-gray-900 dark:text-gray-100 capitalize">{user?.role}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500 dark:text-gray-400">Status</dt>
                  <dd className="font-medium text-green-600 dark:text-green-400">Active</dd>
                </div>
              </dl>
            </CardBody>
          </Card>

          {/* API Keys */}
          <ApiKeysSection />

          {/* Database Backup/Restore - Admin Only */}
          {user?.role === 'admin' && <DatabaseBackupSection />}
        </div>
      </PageContainer>
    </>
  );
}

function ApiKeysSection() {
  const { data: apiKeysResponse, isLoading } = useApiKeys();
  const createApiKey = useCreateApiKey();
  const deleteApiKey = useDeleteApiKey();

  const apiKeys = apiKeysResponse?.data ?? [];

  // Modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createdKey, setCreatedKey] = useState<{ name: string; plaintextKey: string } | null>(null);
  const [copied, setCopied] = useState(false);

  // Revoke confirmation state
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);

  // Form state
  const [keyName, setKeyName] = useState('');
  const [keyExpiration, setKeyExpiration] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!keyName.trim()) return;
    setCreateError(null);

    try {
      const result = await createApiKey.mutateAsync({
        name: keyName.trim(),
        expiresAt: keyExpiration || null,
      });

      if (result) {
        setCreatedKey({ name: result.apiKey.name, plaintextKey: result.plaintextKey });
        setKeyName('');
        setKeyExpiration('');
        setIsCreateModalOpen(false);
      }
    } catch (error: any) {
      setCreateError(error.message ?? 'Failed to create API key');
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    try {
      await deleteApiKey.mutateAsync(revokeTarget.id);
    } catch {
      // Error handled by TanStack Query
    }
    setRevokeTarget(null);
  };

  const handleCopy = async () => {
    if (!createdKey) return;
    await navigator.clipboard.writeText(createdKey.plaintextKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    setKeyName('');
    setKeyExpiration('');
    setCreateError(null);
  };

  const closeKeyDisplay = () => {
    setCreatedKey(null);
    setCopied(false);
  };

  return (
    <>
      <Card>
        <CardHeader className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Key className="h-5 w-5 text-gray-600 dark:text-gray-300" />
            <h2 className="font-semibold">API Keys</h2>
          </div>
          <Button
            size="sm"
            onClick={() => setIsCreateModalOpen(true)}
            leftIcon={<Key className="h-4 w-4" />}
          >
            Generate API Key
          </Button>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading API keys...</p>
          ) : apiKeys.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No API keys yet. Generate one to get started.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-2 pr-4 font-medium text-gray-500 dark:text-gray-400">Name</th>
                    <th className="text-left py-2 pr-4 font-medium text-gray-500 dark:text-gray-400">Key Prefix</th>
                    <th className="text-left py-2 pr-4 font-medium text-gray-500 dark:text-gray-400">Created</th>
                    <th className="text-left py-2 pr-4 font-medium text-gray-500 dark:text-gray-400">Last Used</th>
                    <th className="text-left py-2 pr-4 font-medium text-gray-500 dark:text-gray-400">Status</th>
                    <th className="text-right py-2 font-medium text-gray-500 dark:text-gray-400"></th>
                  </tr>
                </thead>
                <tbody>
                  {apiKeys.map((key) => (
                    <tr key={key.id} className="border-b border-gray-100 dark:border-gray-800 last:border-0">
                      <td className="py-3 pr-4 font-medium text-gray-900 dark:text-gray-100">{key.name}</td>
                      <td className="py-3 pr-4">
                        <code className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded font-mono">
                          {key.keyPrefix}...
                        </code>
                      </td>
                      <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                        {new Date(key.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                        {key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleDateString() : 'Never'}
                      </td>
                      <td className="py-3 pr-4">
                        {key.isActive ? (
                          <Badge variant="green">Active</Badge>
                        ) : (
                          <Badge variant="gray">Revoked</Badge>
                        )}
                      </td>
                      <td className="py-3 text-right">
                        {key.isActive && (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => setRevokeTarget(key)}
                            leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                          >
                            Revoke
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Create API Key Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        title="Generate API Key"
        size="md"
      >
        <div className="space-y-4">
          <Input
            label="Key Name"
            value={keyName}
            onChange={(e) => setKeyName(e.target.value)}
            placeholder="e.g. CI/CD Pipeline, Monitoring Script"
            required
          />
          <div>
            <label className="label text-sm font-medium">Expiration (optional)</label>
            <input
              type="date"
              value={keyExpiration}
              onChange={(e) => setKeyExpiration(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="input mt-1"
            />
          </div>
          {createError && <p className="text-sm text-red-600 dark:text-red-400">{createError}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={closeCreateModal}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              isLoading={createApiKey.isPending}
              disabled={!keyName.trim()}
            >
              Generate Key
            </Button>
          </div>
        </div>
      </Modal>

      {/* Key Display Modal (post-creation) */}
      <Modal isOpen={!!createdKey} onClose={closeKeyDisplay} title="API Key Created" size="md">
        {createdKey && (
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg">
              <div className="flex gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                <p className="text-sm text-amber-800 dark:text-amber-300 font-medium">
                  This key will only be shown once. Copy it now and store it securely.
                </p>
              </div>
            </div>
            <div>
              <label className="label text-sm font-medium">Key Name</label>
              <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">{createdKey.name}</p>
            </div>
            <div>
              <label className="label text-sm font-medium">API Key</label>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 block bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded-lg font-mono text-sm break-all select-all">
                  {createdKey.plaintextKey}
                </code>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleCopy}
                  leftIcon={
                    copied ? (
                      <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )
                  }
                >
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </div>
            <div className="flex justify-end pt-2">
              <Button onClick={closeKeyDisplay}>Done</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Revoke Confirmation */}
      <ConfirmDialog
        isOpen={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onConfirm={handleRevoke}
        title="Revoke API Key"
        message={`Are you sure you want to revoke "${revokeTarget?.name}"? Any integrations using this key will stop working immediately.`}
        confirmLabel="Revoke Key"
        variant="danger"
        isLoading={deleteApiKey.isPending}
      />
    </>
  );
}

function DatabaseBackupSection() {
  const [backupFormat, setBackupFormat] = useState<'sql' | 'custom'>('sql');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [dbInfo, setDbInfo] = useState<{ database: string; size: string } | null>(null);

  // Restore state
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [restoreSuccess, setRestoreSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch database info on mount
  useEffect(() => {
    api
      .get('/admin/backup/info')
      .then((data) => setDbInfo(data as unknown as { database: string; size: string }))
      .catch(() => {}); // Ignore errors
  }, []);

  const handleDownload = async () => {
    setIsDownloading(true);
    setDownloadError(null);

    try {
      const response = await fetch(`/api/admin/backup?format=${backupFormat}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download backup');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename =
        filenameMatch?.[1] ?? `aelita-backup.${backupFormat === 'sql' ? 'sql' : 'dump'}`;

      // Download the file
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      setDownloadError(error instanceof Error ? error.message : 'Download failed');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.sql') && !file.name.endsWith('.dump')) {
        setRestoreError('Only .sql and .dump files are allowed');
        return;
      }
      setSelectedFile(file);
      setRestoreError(null);
      setIsRestoreModalOpen(true);
    }
  };

  const handleRestore = async () => {
    if (!selectedFile || confirmPhrase !== 'REPLACE DATABASE') {
      return;
    }

    setIsRestoring(true);
    setRestoreError(null);
    setRestoreSuccess(false);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('confirmation', confirmPhrase);

      const response = await fetch('/api/admin/restore', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Restore failed');
      }

      setRestoreSuccess(true);
      setIsRestoreModalOpen(false);
      setSelectedFile(null);
      setConfirmPhrase('');

      // Refresh the page after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      setRestoreError(error instanceof Error ? error.message : 'Restore failed');
    } finally {
      setIsRestoring(false);
    }
  };

  const closeRestoreModal = () => {
    setIsRestoreModalOpen(false);
    setSelectedFile(null);
    setConfirmPhrase('');
    setRestoreError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <Card className="border-amber-200 dark:border-amber-800">
        <CardHeader className="flex items-center gap-2">
          <Database className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          <h2 className="font-semibold">Database Backup & Restore</h2>
        </CardHeader>
        <CardBody className="space-y-6">
          {/* Database Info */}
          {dbInfo && (
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Database:</span>
                <span className="font-medium">{dbInfo.database}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-gray-500 dark:text-gray-400">Size:</span>
                <span className="font-medium">{dbInfo.size}</span>
              </div>
            </div>
          )}

          {/* Backup Section */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">Download Backup</h3>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="label text-xs">Format</label>
                <Select
                  value={backupFormat}
                  onChange={(e) => setBackupFormat(e.target.value as 'sql' | 'custom')}
                  options={[
                    { value: 'sql', label: 'SQL (.sql) - Human readable' },
                    { value: 'custom', label: 'Binary (.dump) - Compressed' },
                  ]}
                />
              </div>
              <Button
                onClick={handleDownload}
                isLoading={isDownloading}
                leftIcon={<Download className="h-4 w-4" />}
              >
                Download
              </Button>
            </div>
            {downloadError && <p className="text-sm text-red-600 dark:text-red-400 mt-2">{downloadError}</p>}
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Downloads a complete backup of the database including all projects, clients,
              employees, tasks, and time entries.
            </p>
          </div>

          {/* Restore Section */}
          <div className="pt-4 border-t">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">Restore from Backup</h3>
            <div className="p-4 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg mb-3">
              <div className="flex gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                <div className="text-sm text-amber-800 dark:text-amber-300">
                  <p className="font-medium">Warning: This will replace all data</p>
                  <p className="mt-1">
                    Restoring from a backup will completely replace the current database. This
                    action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".sql,.dump"
              className="hidden"
            />
            <Button
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              leftIcon={<Upload className="h-4 w-4" />}
            >
              Select Backup File
            </Button>
            {restoreSuccess && (
              <p className="text-sm text-green-600 dark:text-green-400 mt-2">
                Database restored successfully! Reloading page...
              </p>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Restore Confirmation Modal */}
      <Modal
        isOpen={isRestoreModalOpen}
        onClose={closeRestoreModal}
        title="Confirm Database Restore"
        size="md"
      >
        <div className="space-y-4">
          <div className="p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
              <div className="text-sm text-red-800 dark:text-red-300">
                <p className="font-medium">This will permanently replace all data!</p>
                <p className="mt-1">
                  All current projects, clients, employees, tasks, and time entries will be deleted
                  and replaced with the data from the backup file.
                </p>
              </div>
            </div>
          </div>

          {selectedFile && (
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">File:</span>
                <span className="font-medium">{selectedFile.name}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-gray-500 dark:text-gray-400">Size:</span>
                <span className="font-medium">{(selectedFile.size / 1024).toFixed(1)} KB</span>
              </div>
            </div>
          )}

          <div>
            <label className="label">
              Type <span className="font-mono font-bold">REPLACE DATABASE</span> to confirm
            </label>
            <Input
              value={confirmPhrase}
              onChange={(e) => setConfirmPhrase(e.target.value)}
              placeholder="Type confirmation phrase..."
              autoComplete="off"
            />
          </div>

          {restoreError && <p className="text-sm text-red-600 dark:text-red-400">{restoreError}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={closeRestoreModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleRestore}
              isLoading={isRestoring}
              disabled={confirmPhrase !== 'REPLACE DATABASE'}
              className="bg-red-600 hover:bg-red-700"
            >
              Replace Database
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
